/* ==========================================================================
   SPICY HOME — enquire-tier corrections
   ==========================================================================
   Two separate defects are handled here. Both were confirmed on the live
   storefront, neither is theoretical.

   ---------------------------------------------------------------------------
   DEFECT 1 — the labels said "Free" and "SOLD OUT"
   ---------------------------------------------------------------------------
   Enquire-tier products (Vispring, Pedrali, and the whole no-price import)
   carry price 0.00 plus an `Enquire` tag, and no inventory. The Liquid renders
   exactly the right thing in both places:

     <span data-product-price class="… product__price--enquire">Price on Enquiry</span>
     <a class="… product__submit__enquire"><span data-add-to-cart-text>Enquire About This Piece</span></a>

   Palo Alto's theme.js then repaints both from the product JSON:
     • price 0      -> window.theme.strings.free     -> "Free"
     • inventory 0  -> window.theme.strings.sold_out -> "SOLD OUT"

   So a EUR 5,000 Vispring bed advertised itself as "Free" with a "SOLD OUT"
   button.

   Fixed here rather than in theme.js because that is the 648 KB vendor bundle
   and editing it forfeits future theme updates. Not fixed by overriding
   window.theme.strings.* because those are shared with the cart, so a real
   free gift or a genuinely sold-out chair would then be mislabelled. Not fixed
   in CSS because hiding text and painting a replacement with ::after leaves the
   original text node in the accessibility tree — a screen reader would still
   announce "Free". The text itself has to change, which means JS.

   ---------------------------------------------------------------------------
   DEFECT 2 — the enquiry email went to Dimitris's private admin address
   ---------------------------------------------------------------------------
   The enquire button is built from {{ shop.email }}, which is the Shopify
   ACCOUNT owner address: dimitris@spicyhomestudio.com. That is not the
   customer-facing mailbox. Every enquiry from every product was being sent to
   his personal admin inbox instead of the shared one the business actually
   reads, which is info@spicyhomestudio.com (the address published in the
   footer, footer -> contact -> email).

   Rewritten here, in JS, deliberately: the address is generated deep inside
   snippets/product.liquid (80 KB), and re-emitting that whole file by hand to
   change one token is a far bigger risk to a live sales path than this is.
   If it fails, the link still reaches Dimitris — just at the wrong address —
   so the failure mode is graceful.

   THE DURABLE FIX, when someone has the file open anyway: replace
   {{ shop.email }} in the enquire mailto inside snippets/product.liquid with a
   proper theme setting defaulting to info@spicyhomestudio.com, then delete the
   rewriteEnquiryAddress() call below. Nothing else here needs to change.

   ---------------------------------------------------------------------------
   ORDERING — why the label capture is always correct
   ---------------------------------------------------------------------------
   This file is loaded from snippets/spicy-head.liquid, ahead of theme.js in
   document order. Deferred scripts execute in that order and all of them
   finish before DOMContentLoaded, which is when theme.js does its repaint. So
   the value snapshotted below is always the server-rendered Liquid one, never
   the clobbered one.
   ========================================================================== */

(function () {
  'use strict';

  /* The mailbox the business actually reads. Not shop.email, which is the
     Shopify account owner's private address. */
  var ENQUIRY_ADDRESS = 'info@spicyhomestudio.com';

  var LABEL_SELECTORS = [
    /* product page price */
    '[data-product-price].product__price--enquire',
    /* collection / grid card price */
    '.product-grid-item__price--enquire',
    /* the enquire button's own label, which theme.js turns into SOLD OUT */
    '.product__submit__enquire [data-add-to-cart-text]'
  ].join(',');

  /* Every enquire call-to-action, on the product page and on grid cards. */
  var MAILTO_SELECTORS = [
    'a.product__submit__enquire[href^="mailto:"]',
    'a.btn--quick-enquire[href^="mailto:"]'
  ].join(',');

  /* WeakMap so nodes torn out by AJAX (filters, infinite scroll, quickview)
     are garbage collected instead of leaking observers. */
  var known = new WeakMap();

  function guardLabel(el) {
    if (known.has(el)) return;

    /* Prefer an explicit attribute if the theme ever renders one; otherwise
       trust the server-rendered text, which at this point is still correct. */
    var label = (el.getAttribute('data-enquire-label') || el.textContent || '').trim();
    if (!label) return;

    known.set(el, label);

    var mo = new MutationObserver(function () {
      /* The equality check is what stops this recursing: our own write fires
         the observer again, sees the text already matches, and stops. */
      if (el.textContent.trim() !== label) {
        el.textContent = label;
      }
    });
    mo.observe(el, { childList: true, characterData: true, subtree: true });
  }

  function rewriteEnquiryAddress(el) {
    var href = el.getAttribute('href') || '';
    /* Swap only the address, never the subject/body — those are built from the
       product title and are already correct. */
    var fixed = href.replace(/^mailto:[^?]*/, 'mailto:' + ENQUIRY_ADDRESS);
    if (fixed !== href) el.setAttribute('href', fixed);
  }

  function scan() {
    var labels = document.querySelectorAll(LABEL_SELECTORS);
    for (var i = 0; i < labels.length; i++) guardLabel(labels[i]);

    var links = document.querySelectorAll(MAILTO_SELECTORS);
    for (var j = 0; j < links.length; j++) rewriteEnquiryAddress(links[j]);
  }

  scan();
  document.addEventListener('DOMContentLoaded', scan);

  /* Grid cards, quickview and filtered collection pages inject their markup
     after load, so new enquire nodes keep appearing. Rescan on DOM changes,
     coalesced to one pass per frame so a busy page is not doing this per node. */
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();